import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { getMyVenueOnboarding, saveVenueOnboarding } from "@/lib/venue-onboarding.functions";
import type { VerifiedAddress } from "@/lib/places.functions";

export const Route = createFileRoute("/_authenticated/venue/onboarding")({
  head: () => ({
    meta: [{ title: "Venue Onboarding — The Hospitality Reserve" }],
  }),
  component: VenueOnboardingWizard,
});

type StepKey = "business" | "contact" | "address" | "review";
const STEPS: { key: StepKey; label: string }[] = [
  { key: "business", label: "Business" },
  { key: "contact", label: "Contact" },
  { key: "address", label: "Address" },
  { key: "review", label: "Review" },
];

function VenueOnboardingWizard() {
  const navigate = useNavigate();
  const load = useServerFn(getMyVenueOnboarding);
  const save = useServerFn(saveVenueOnboarding);

  const [hydrating, setHydrating] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [companiesHouse, setCompaniesHouse] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState<VerifiedAddress | null>(null);
  const [initialAddressQuery, setInitialAddressQuery] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      const r = await load();
      if ("error" in r) {
        toast.error(r.error);
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      const p = r.profile;
      // Already approved / rejected → cannot edit further.
      if (p.onboarding_status === "approved") {
        navigate({ to: "/venue", replace: true });
        return;
      }
      if (p.onboarding_status === "rejected") {
        navigate({ to: "/venue-pending", replace: true });
        return;
      }
      setIsEdit(Boolean(p.venue_onboarding_completed_at));
      setBusinessName(p.business_name ?? p.headline ?? "");
      setLegalName((p as { legal_trading_name?: string | null }).legal_trading_name ?? "");
      setCompaniesHouse((p as { companies_house_number?: string | null }).companies_house_number ?? "");
      setBusinessEmail(p.business_email ?? "");
      setPhone(p.phone_number ?? "");
      if (p.address_line1) {
        setAddress({
          place_id: p.place_id ?? "",
          formatted: [p.address_line1, p.address_line2, p.city, p.postcode]
            .filter(Boolean)
            .join(", "),
          address_line1: p.address_line1,
          address_line2: p.address_line2 ?? "",
          city: p.city ?? "",
          postcode: p.postcode ?? "",
          country: p.country ?? "United Kingdom",
          latitude: null,
          longitude: null,
        });
        setInitialAddressQuery(
          [p.address_line1, p.city, p.postcode].filter(Boolean).join(", "),
        );
      } else if (p.address) {
        setInitialAddressQuery(p.address);
      }
      setHydrating(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = STEPS[stepIdx].key;

  const validateStep = (): boolean => {
    const e: Record<string, string> = {};
    if (step === "business") {
      if (businessName.trim().length < 2) e.businessName = "Business name is required.";
      if (legalName.trim().length < 2) {
        e.legalName = "Registered legal / trading name is required.";
      }
      if (companiesHouse.trim() && !/^[A-Z0-9]{6,10}$/i.test(companiesHouse.trim())) {
        e.companiesHouse = "Enter a valid Companies House number (6-10 characters).";
      }
    }
    if (step === "contact") {
      if (!/^\S+@\S+\.\S+$/.test(businessEmail.trim())) e.businessEmail = "Enter a valid email.";
      if (!/^[0-9+()\-\s]{7,}$/.test(phone.trim())) e.phone = "Enter a valid phone number.";
    }
    if (step === "address" && !address) {
      e.address = "Pick your address from the verified suggestions.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const handleBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    if (!address) return;
    setSubmitting(true);
    try {
      const r = await save({
        data: {
          business_name: businessName.trim(),
          legal_trading_name: legalName.trim(),
          companies_house_number: companiesHouse.trim().toUpperCase() || null,
          business_email: businessEmail.trim(),
          phone_number: phone.trim(),
          address_line1: address.address_line1,
          address_line2: address.address_line2 || null,
          city: address.city,
          postcode: address.postcode,
          country: address.country || "United Kingdom",
          place_id: address.place_id || null,
        },
      });
      if ("error" in r) throw new Error(r.error);
      toast.success(
        isEdit
          ? "Updated. Your venue details are still under review."
          : "Thanks! Your venue details are with our team for review.",
      );
      navigate({ to: "/venue-pending", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save your details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (hydrating) {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  return (
    <div className="min-h-dvh bg-background">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-2xl px-6 py-12">
        <p className="tracking-wide-luxury text-accent">
          {isEdit ? "Update Venue Details" : "Venue Onboarding"}
        </p>
        <h1 className="mt-3 font-display text-3xl">
          {isEdit ? "Correct your application" : "Tell us about your business"}
        </h1>
        <span className="gold-divider mt-4" />
        {isEdit && (
          <p className="mt-4 text-sm text-muted-foreground">
            You can edit any field until an admin makes a decision on your application.
          </p>
        )}

        <ol
          aria-label="Onboarding steps"
          className="mt-8 grid grid-cols-4 gap-2 text-xs tracking-wide-luxury"
        >
          {STEPS.map((s, i) => (
            <li
              key={s.key}
              aria-current={i === stepIdx ? "step" : undefined}
              className={`border-t-2 pt-3 ${
                i <= stepIdx ? "border-accent text-accent" : "border-border text-muted-foreground"
              }`}
            >
              <span className="block text-[10px]">Step {i + 1}</span>
              {s.label}
              <span className="sr-only">
                {i < stepIdx ? " (completed)" : i === stepIdx ? " (current step)" : " (not started)"}
              </span>
            </li>
          ))}
        </ol>

        <p aria-live="polite" className="sr-only">
          Step {stepIdx + 1} of {STEPS.length}: {STEPS[stepIdx].label}
        </p>

        <section
          aria-labelledby="venue-onboarding-step-heading"
          className="mt-8 border border-border bg-card p-6 space-y-5"
        >
          <h2 id="venue-onboarding-step-heading" className="sr-only">
            {STEPS[stepIdx].label}
          </h2>
          {step === "business" && (
            <div className="space-y-2">
              <Label htmlFor="business_name">Business name</Label>
              <Input
                id="business_name"
                aria-invalid={Boolean(errors.businessName)}
                aria-describedby={errors.businessName ? "business_name-error" : undefined}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={120}
                placeholder="e.g. The Marigold Hotel Ltd"
                className="rounded-none"
              />
              {errors.businessName && (
                <p id="business_name-error" role="alert" className="text-xs text-destructive">{errors.businessName}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Use your registered trading name. This is shown to bartenders on every shift you post.
              </p>

              <div className="space-y-2 pt-4">
                <Label htmlFor="legal_trading_name">Legal / registered company name</Label>
                <Input
                  id="legal_trading_name"
                  aria-invalid={Boolean(errors.legalName)}
                  aria-describedby={errors.legalName ? "legal_trading_name-error" : undefined}
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  maxLength={160}
                  placeholder="e.g. Marigold Hospitality Group Limited"
                  className="rounded-none"
                />
                {errors.legalName && (
                  <p id="legal_trading_name-error" role="alert" className="text-xs text-destructive">{errors.legalName}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Exactly as registered at Companies House. Our team cross-checks this before approving your account.
                </p>
              </div>

              <div className="space-y-2 pt-4">
                <Label htmlFor="companies_house_number">Companies House number (optional)</Label>
                <Input
                  id="companies_house_number"
                  aria-invalid={Boolean(errors.companiesHouse)}
                  aria-describedby={errors.companiesHouse ? "companies_house_number-error" : undefined}
                  value={companiesHouse}
                  onChange={(e) => setCompaniesHouse(e.target.value.toUpperCase())}
                  maxLength={16}
                  placeholder="12345678"
                  className="rounded-none"
                />
                {errors.companiesHouse && (
                  <p id="companies_house_number-error" role="alert" className="text-xs text-destructive">{errors.companiesHouse}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Speeds up verification. Leave blank if you trade as a sole trader or partnership.
                </p>
              </div>
            </div>
          )}

          {step === "contact" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="business_email">Business email</Label>
                <Input
                  id="business_email"
                  aria-invalid={Boolean(errors.businessEmail)}
                  aria-describedby={errors.businessEmail ? "business_email-error" : undefined}
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  maxLength={255}
                  placeholder="bookings@yourvenue.co.uk"
                  className="rounded-none"
                />
                {errors.businessEmail && (
                  <p id="business_email-error" role="alert" className="text-xs text-destructive">{errors.businessEmail}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={32}
                  placeholder="+44 20 7946 0958"
                  className="rounded-none"
                />
                {errors.phone && <p id="phone-error" role="alert" className="text-xs text-destructive">{errors.phone}</p>}
              </div>
            </>
          )}

          {step === "address" && (
            <>
              <AddressAutocomplete
                label="Business address"
                initialQuery={initialAddressQuery}
                initialVerified={address}
                onVerified={setAddress}
                disabled={submitting}
              />
              {errors.address && <p role="alert" className="text-xs text-destructive">{errors.address}</p>}
              {address && (
                <div className="mt-3 border border-border bg-muted/20 p-3 text-xs text-muted-foreground space-y-0.5">
                  <p>{address.address_line1}</p>
                  {address.address_line2 && <p>{address.address_line2}</p>}
                  <p>
                    {address.city} {address.postcode}
                  </p>
                  <p>{address.country}</p>
                </div>
              )}
            </>
          )}

          {step === "review" && (
            <dl className="space-y-3 text-sm">
              <Row label="Business" value={businessName} />
              <Row label="Legal name" value={legalName} />
              <Row label="Companies House" value={companiesHouse || "—"} />
              <Row label="Email" value={businessEmail} />
              <Row label="Phone" value={phone} />
              <Row label="Address" value={address?.formatted ?? ""} />
              <Row label="Postcode" value={address?.postcode ?? ""} />
              <p className="pt-2 text-xs text-muted-foreground">
                {isEdit
                  ? "Saving will update the version our team is reviewing."
                  : "After you submit, our team will review your application. You will be notified once it has been approved."}
              </p>
            </dl>
          )}
        </section>

        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={stepIdx === 0 || submitting}
            className="rounded-none tracking-wide-luxury"
          >
            Back
          </Button>
          {step !== "review" ? (
            <Button
              onClick={handleNext}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !address}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Submit for review"}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border pb-2">
      <dt className="text-xs tracking-wide-luxury text-muted-foreground">{label}</dt>
      <dd className="text-right">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}
